import React, { useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import api from "../../../services/api";
import "./DirectorioEditor.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
const BACKEND_ORIGIN = API_BASE.replace(/\/api\/?$/, "");

const CARGOS = [
  { code: "PRESIDENTE", label: "Presidente" },
  { code: "VICEPRESIDENTE", label: "Vicepresidente" },
  { code: "SECRETARIA_FINANZAS", label: "Secretaría de Finanzas" },
  { code: "SECRETARIA_ACTAS", label: "Secretaría de Actas" },
  { code: "ASESORIA_TECNICA", label: "Asesoría Técnico-Científica" },
  { code: "VOCAL_1", label: "Vocal 1" },
  { code: "VOCAL_2", label: "Vocal 2" },
];

const getErrorMessage = (error, fallback) => {
  if (typeof error?.response?.data === "string") return error.response.data;
  if (typeof error?.response?.data?.detail === "string") return error.response.data.detail;
  return fallback;
};

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return "";
  if (/^https?:\/\//i.test(avatar)) return avatar;
  if (avatar.startsWith("/")) return `${BACKEND_ORIGIN}${avatar}`;
  return `${BACKEND_ORIGIN}/${avatar}`;
};

function DirectorioEditor({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchState, setSearchState] = useState({});
  const [feedback, setFeedback] = useState(null);
  const debounceTimers = useRef({});

  const authConfig = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : undefined),
    [token]
  );

  const rowsByCargo = useMemo(() => {
    const index = {};
    rows.forEach((row) => {
      index[row.cargo] = row;
    });
    return index;
  }, [rows]);

  useEffect(() => {
    const fetchDirectorio = async () => {
      setLoading(true);
      setFeedback(null);
      try {
        const { data } = await api.get("/directorio/", authConfig);
        const apiRows = Array.isArray(data) ? data : [];
        const apiMap = {};
        apiRows.forEach((item) => {
          if (!item?.cargo) return;
          apiMap[item.cargo] = item;
        });

        const normalizedRows = CARGOS.map((cargoItem) => {
          const match = apiMap[cargoItem.code];
          return {
            cargo: cargoItem.code,
            cargoLabel: cargoItem.label,
            socioId: match?.socio_id || "",
            socioNombre: match?.socio_nombre || "",
            socioAvatar: resolveAvatarUrl(match?.socio_avatar),
          };
        });
        setRows(normalizedRows);

        const initialSearchState = {};
        normalizedRows.forEach((row) => {
          initialSearchState[row.cargo] = {
            query: row.socioNombre || "",
            results: [],
            open: false,
            loading: false,
          };
        });
        setSearchState(initialSearchState);
      } catch (error) {
        setFeedback({
          type: "error",
          message: getErrorMessage(error, "No se pudo cargar el directorio."),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDirectorio();
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, [authConfig]);

  const runSearch = async (cargo, query) => {
    if (!query || query.trim().length < 2) {
      setSearchState((prev) => ({
        ...prev,
        [cargo]: {
          ...(prev[cargo] || {}),
          query,
          results: [],
          open: false,
          loading: false,
        },
      }));
      return;
    }

    setSearchState((prev) => ({
      ...prev,
      [cargo]: {
        ...(prev[cargo] || {}),
        query,
        loading: true,
        open: true,
      },
    }));

    try {
      const { data } = await api.get("/socios/buscar/", {
        ...authConfig,
        params: { q: query.trim() },
      });

      const results = (Array.isArray(data) ? data : []).map((item) => ({
        id: item?.id || "",
        nombre: item?.nombre || "",
        avatar: resolveAvatarUrl(item?.avatar),
      }));

      setSearchState((prev) => ({
        ...prev,
        [cargo]: {
          ...(prev[cargo] || {}),
          query,
          results,
          open: true,
          loading: false,
        },
      }));
    } catch (error) {
      setSearchState((prev) => ({
        ...prev,
        [cargo]: {
          ...(prev[cargo] || {}),
          query,
          results: [],
          open: false,
          loading: false,
        },
      }));
      setFeedback({
        type: "error",
        message: getErrorMessage(error, "No se pudo buscar socios."),
      });
    }
  };

  const handleQueryChange = (cargo, value) => {
    setSearchState((prev) => ({
      ...prev,
      [cargo]: {
        ...(prev[cargo] || {}),
        query: value,
      },
    }));

    if (debounceTimers.current[cargo]) {
      clearTimeout(debounceTimers.current[cargo]);
    }

    debounceTimers.current[cargo] = setTimeout(() => {
      runSearch(cargo, value);
    }, 300);
  };

  const handleSelectSocio = (cargo, socio) => {
    setRows((prev) =>
      prev.map((row) =>
        row.cargo === cargo
          ? {
              ...row,
              socioId: socio.id,
              socioNombre: socio.nombre,
              socioAvatar: socio.avatar,
            }
          : row
      )
    );

    setSearchState((prev) => ({
      ...prev,
      [cargo]: {
        ...(prev[cargo] || {}),
        query: socio.nombre,
        results: [],
        open: false,
        loading: false,
      },
    }));
  };

  const handleClearSocio = (cargo) => {
    setRows((prev) =>
      prev.map((row) =>
        row.cargo === cargo
          ? { ...row, socioId: "", socioNombre: "", socioAvatar: "" }
          : row
      )
    );
    setSearchState((prev) => ({
      ...prev,
      [cargo]: {
        ...(prev[cargo] || {}),
        query: "",
        results: [],
        open: false,
        loading: false,
      },
    }));
  };

  const handleSaveDirectorio = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = CARGOS.map((cargoItem) => ({
        cargo: cargoItem.code,
        socio_id: rowsByCargo[cargoItem.code]?.socioId || null,
      }));
      await api.put("/directorio/", payload, authConfig);
      setFeedback({ type: "success", message: "Directorio guardado correctamente." });
    } catch (error) {
      setFeedback({
        type: "error",
        message: getErrorMessage(error, "No se pudo guardar el directorio."),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="directorio-editor-loading">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="directorio-editor">
      {feedback && (
        <Alert severity={feedback.type} className="directorio-editor-alert">
          {feedback.message}
        </Alert>
      )}

      <div className="directorio-editor-rows">
        {CARGOS.map((cargoItem) => {
          const row = rowsByCargo[cargoItem.code] || {
            cargo: cargoItem.code,
            cargoLabel: cargoItem.label,
            socioId: "",
            socioNombre: "",
            socioAvatar: "",
          };
          const state = searchState[cargoItem.code] || {
            query: "",
            results: [],
            open: false,
            loading: false,
          };
          const showDropdown = state.open && (state.loading || state.results.length > 0 || state.query.trim().length >= 2);

          return (
            <div key={cargoItem.code} className="directorio-editor-row">
              <div className="directorio-editor-cargo">
                <Typography variant="subtitle2">{cargoItem.label}</Typography>
              </div>

              <div className="directorio-editor-search">
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar socio"
                  value={state.query}
                  onFocus={() =>
                    setSearchState((prev) => ({
                      ...prev,
                      [cargoItem.code]: {
                        ...(prev[cargoItem.code] || state),
                        open: true,
                      },
                    }))
                  }
                  onBlur={() =>
                    setTimeout(() => {
                      setSearchState((prev) => ({
                        ...prev,
                        [cargoItem.code]: {
                          ...(prev[cargoItem.code] || state),
                          open: false,
                        },
                      }));
                    }, 120)
                  }
                  onChange={(event) => handleQueryChange(cargoItem.code, event.target.value)}
                />

                {showDropdown && (
                  <div className="directorio-editor-dropdown">
                    {state.loading ? (
                      <div className="directorio-editor-dropdown-item muted">Buscando...</div>
                    ) : state.results.length === 0 ? (
                      <div className="directorio-editor-dropdown-item muted">Sin resultados</div>
                    ) : (
                      state.results.map((socio) => (
                        <button
                          key={`${cargoItem.code}-${socio.id}`}
                          type="button"
                          className="directorio-editor-dropdown-item"
                          onClick={() => handleSelectSocio(cargoItem.code, socio)}
                        >
                          <Avatar src={socio.avatar} alt={socio.nombre} />
                          <span>{socio.nombre}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div className="directorio-editor-selected">
                {row.socioNombre ? (
                  <div className="directorio-editor-selected-content">
                    <Avatar
                      src={row.socioAvatar}
                      alt={row.socioNombre}
                      className="directorio-editor-avatar"
                    />
                    <span>{row.socioNombre}</span>
                    <button
                      type="button"
                      className="directorio-editor-clear-btn"
                      onClick={() => handleClearSocio(cargoItem.code)}
                    >
                      Quitar
                    </button>
                  </div>
                ) : (
                  <span className="directorio-editor-empty">Sin socio asignado</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="directorio-editor-actions">
        <Button variant="contained" onClick={handleSaveDirectorio} disabled={saving}>
          {saving ? "Guardando..." : "Guardar Directorio"}
        </Button>
      </div>
    </div>
  );
}

export default DirectorioEditor;
